import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    Typography,
    TextField,
    FormControlLabel,
    Checkbox,
    Alert,
    CircularProgress,
    Radio,
    RadioGroup,
    IconButton,
    styled,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Tooltip from '@mui/material/Tooltip';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userQuestionnaireService, IUserQuestionnaireAssignment, IQuestionnaire, IQuestionnaireField } from '../services/questionnaireService';
import { useAuthStore } from '../stores/authStore';
import AiHelperWidget from '../components/AiHelperWidget';

// Styled Components matching WelcomeQuestionnaire design
const PageContainer = styled(Box)({
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
});

const Header = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 40px',
    borderBottom: '1px solid #ebebeb',
    position: 'sticky',
    top: 0,
    backgroundColor: '#ffffff',
    zIndex: 100,
});

const Logo = styled(Typography)({
    fontSize: '24px',
    fontWeight: 700,
    color: '#222222',
    fontFamily: '"Circular", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
});

const HeaderActions = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
});

const HeaderButton = styled(Button)({
    borderRadius: '20px',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '14px',
    padding: '8px 16px',
    color: '#222222',
    border: '1px solid #222222',
    '&:hover': {
        backgroundColor: '#f7f7f7',
        border: '1px solid #222222',
    },
});

const MainContent = styled(Box)({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '60px 40px 120px',
    maxWidth: '640px',
    margin: '0 auto',
    width: '100%',
});

const PageTitle = styled(Typography)({
    fontSize: '22px',
    fontWeight: 600,
    color: '#222222',
    marginBottom: '32px',
    fontFamily: '"Circular", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
    lineHeight: 1.25,
});

const SectionTitle = styled(Typography)({
    fontSize: '16px',
    fontWeight: 600,
    color: '#222222',
    marginBottom: '24px',
    fontFamily: '"Circular", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
});

const FieldContainer = styled(Box)({
    width: '100%',
    marginBottom: '24px',
});

const FieldRow = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
    borderBottom: '1px solid #ebebeb',
});

const FieldLabel = styled(Typography)({
    fontSize: '16px',
    color: '#222222',
    fontFamily: '"Circular", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
});

const StyledTextField = styled(TextField)({
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        '& fieldset': {
            borderColor: '#b0b0b0',
        },
        '&:hover fieldset': {
            borderColor: '#222222',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#222222',
            borderWidth: '2px',
        },
    },
    '& .MuiInputBase-input': {
        padding: '14px 16px',
        fontSize: '16px',
    },
});

const Footer = styled(Box)({
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#ffffff',
    borderTop: '1px solid #ebebeb',
    zIndex: 100,
});

const ProgressContainer = styled(Box)({
    display: 'flex',
    gap: '4px',
    padding: '0 40px',
});

const ProgressSegment = styled(Box)<{ active?: boolean; completed?: boolean }>(({ active, completed }) => ({
    flex: 1,
    height: '4px',
    backgroundColor: completed ? '#222222' : active ? '#222222' : '#dddddd',
    transition: 'background-color 0.3s ease',
}));

const FooterActions = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 40px',
});

const BackButton = styled(Button)({
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '16px',
    color: '#222222',
    textDecoration: 'underline',
    padding: '12px 0',
    '&:hover': {
        backgroundColor: 'transparent',
        textDecoration: 'underline',
    },
    '&:disabled': {
        color: '#dddddd',
    },
});

const NextButton = styled(Button)({
    backgroundColor: '#222222',
    color: '#ffffff',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '16px',
    padding: '14px 24px',
    minWidth: '100px',
    '&:hover': {
        backgroundColor: '#000000',
    },
    '&:disabled': {
        backgroundColor: '#dddddd',
        color: '#ffffff',
    },
});

const StuckCheckbox = styled(FormControlLabel)({
    marginLeft: 0,
    '& .MuiTypography-root': {
        fontSize: '14px',
        color: '#717171',
    },
});

const ReviewCard = styled(Box)({
    backgroundColor: '#f7f7f7',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
});

const QuestionnaireAnswer: React.FC = () => {
    const navigate = useNavigate();
    const { assignmentId } = useParams<{ assignmentId: string }>();
    const queryClient = useQueryClient();
    const { checkPendingQuestionnaires, user } = useAuthStore();

    const [sections, setSections] = useState<string[]>([]);
    const [activeSection, setActiveSection] = useState(0);
    const [activeStepInSection, setActiveStepInSection] = useState(0);
    const [responses, setResponses] = useState<Record<string, any>>({});
    const [stuckFields, setStuckFields] = useState<Set<string>>(new Set());
    const [showAiWidget, setShowAiWidget] = useState(false);
    const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    // Fetch assignment
    const { data: assignment, isLoading } = useQuery<IUserQuestionnaireAssignment>({
        queryKey: ['questionnaireAssignment', assignmentId],
        queryFn: () => userQuestionnaireService.getAssignment(assignmentId!),
        enabled: !!assignmentId
    });

    const questionnaire = assignment?.questionnaireId as IQuestionnaire | undefined;
    const steps = questionnaire?.steps || [];
    const isCompleted = assignment?.status === 'completed';

    // Get steps for current section
    const currentSectionSteps = steps.filter(s => s.section === sections[activeSection]);

    // Initialize sections and responses
    useEffect(() => {
        if (questionnaire?.steps) {
            const uniqueSections = Array.from(new Set(questionnaire.steps.map(s => s.section || 'General')));
            setSections(uniqueSections);
        }
    }, [questionnaire]);

    useEffect(() => {
        if (assignment?.responses) {
            setResponses(assignment.responses);
        }
    }, [assignment]);

    // Save mutation
    const saveMutation = useMutation({
        mutationFn: ({ isComplete }: { isComplete: boolean }) =>
            userQuestionnaireService.saveResponses(assignmentId!, responses, isComplete),
        onSuccess: (data: any, variables) => {
            queryClient.invalidateQueries({ queryKey: ['questionnaireAssignment', assignmentId] });
            queryClient.invalidateQueries({ queryKey: ['myQuestionnaireAssignments'] });
            if (variables.isComplete) {
                setNotification({ type: 'success', message: 'Questionnaire completed successfully!' });
                checkPendingQuestionnaires(); // Re-check pending status to unlock features
                if (data?.documentId) {
                    setTimeout(() => navigate(`/document-library/documents/${data.documentId}`), 1000);
                } else {
                    setTimeout(() => navigate('/assigned-questionnaires'), 1500);
                }
            } else {
                setNotification({ type: 'success', message: 'Progress saved' });
            }
        },
        onError: () => {
            setNotification({ type: 'error', message: 'Failed to save' });
        }
    });

    const validateStep = (stepIndex: number): boolean => {
        const step = currentSectionSteps[stepIndex];
        if (!step) return false;

        let isValid = true;
        for (const field of step.fields) {
            const isStuck = stuckFields.has(field.id);
            const value = responses[field.id];

            if (field.required && !isStuck) {
                if (field.type === 'checkbox') {
                    if (!value) isValid = false;
                } else if (field.type !== 'display' && (!value || value === '')) {
                    isValid = false;
                }
            }
        }

        if (!isValid) {
            alert('Please fill in all required fields or mark them as "I need help".');
        }
        return isValid;
    };

    const handleNext = async () => {
        if (!validateStep(activeStepInSection)) return;
        const completing = activeStepInSection === currentSectionSteps.length - 1 && activeSection === sections.length - 1;
        try {
            await saveMutation.mutateAsync({ isComplete: completing });
            if (activeStepInSection < currentSectionSteps.length - 1) {
                setActiveStepInSection(prev => prev + 1);
            } else if (activeSection < sections.length - 1) {
                setActiveSection(prev => prev + 1);
                setActiveStepInSection(0);
            }
        } catch { /* The mutation displays the error and keeps the draft open. */ }
    };

    const handleBack = () => {
        if (activeStepInSection > 0) {
            setActiveStepInSection(prev => prev - 1);
        } else if (activeSection > 0) {
            setActiveSection(prev => prev - 1);
            const prevSection = sections[activeSection - 1];
            const prevSteps = steps.filter(s => s.section === prevSection);
            setActiveStepInSection(prevSteps.length - 1);
        }
    };

    const handleFieldChange = (fieldId: string, value: any) => {
        setResponses(prev => ({ ...prev, [fieldId]: value }));
    };

    const toggleStuck = (fieldId: string) => {
        setStuckFields(prev => {
            const next = new Set(prev);
            if (next.has(fieldId)) {
                next.delete(fieldId);
            } else {
                next.add(fieldId);
                handleFieldChange(fieldId, '');
            }
            return next;
        });
    };

    const handleSaveAndExit = async () => {
        try {
            await saveMutation.mutateAsync({ isComplete: false });
            navigate('/assigned-questionnaires');
        } catch { /* Keep unsaved answers visible when saving fails. */ }
    };

    if (isLoading) {
        return (
            <PageContainer sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress sx={{ color: '#222222' }} />
            </PageContainer>
        );
    }

    if (!assignment || !questionnaire) {
        return (
            <PageContainer sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Alert severity="error" sx={{ maxWidth: 400 }}>Questionnaire not found</Alert>
                <Button sx={{ mt: 2 }} onClick={() => navigate('/assigned-questionnaires')}>
                    Back to Questionnaires
                </Button>
            </PageContainer>
        );
    }

    if (isCompleted) {
        return (
            <PageContainer sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Alert severity="success" sx={{ maxWidth: 400, mb: 2 }}>
                    This questionnaire has been completed.
                </Alert>
                {user?.userType === 'EO' && <Button sx={{ mb: 2 }} variant="outlined"
                    onClick={() => navigate(`/welcome-questionnaire?assignmentId=${assignmentId}&assignLawyer=1`)}>
                    Assign to Lawyer
                </Button>}
                <Button variant="contained" onClick={() => navigate('/assigned-questionnaires')}>
                    Back to Questionnaires
                </Button>
            </PageContainer>
        );
    }

    const activeStepDef = currentSectionSteps[activeStepInSection];

    if (!activeStepDef) {
        return (
            <PageContainer sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress sx={{ color: '#222222' }} />
            </PageContainer>
        );
    }

    const isFirstStep = activeStepInSection === 0 && activeSection === 0;
    const isLastStep = activeSection === sections.length - 1 && activeStepInSection === currentSectionSteps.length - 1;

    // Calculate total progress
    const completedSteps = steps.findIndex(s => s.id === activeStepDef.id);

    const renderField = (field: IQuestionnaireField) => {
        const isStuck = stuckFields.has(field.id);

        if (field.type === 'text' || field.type === 'number' || field.type === 'email' || field.type === 'date') {
            return (
                <Box>
                    <FieldRow sx={{ borderBottom: 'none', paddingBottom: '8px' }}>
                        <FieldLabel>
                            {field.label} {field.required && <span style={{ color: '#e31c5f' }}>*</span>}
                        </FieldLabel>
                        {field.tooltip && (
                            <Tooltip title={field.tooltip} arrow placement="top">
                                <IconButton size="small" sx={{ color: '#717171', ml: 0.5 }}>
                                    <HelpOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </FieldRow>

                    {!isStuck && (
                        <StyledTextField
                            fullWidth
                            type={field.type}
                            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                            value={responses[field.id] || ''}
                            onChange={(e) => handleFieldChange(field.id, e.target.value)}
                            disabled={isStuck}
                            variant="outlined"
                            InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                        />
                    )}

                    <StuckCheckbox
                        control={
                            <Checkbox
                                checked={isStuck}
                                onChange={() => toggleStuck(field.id)}
                                sx={{
                                    color: '#717171',
                                    '&.Mui-checked': { color: '#222222' },
                                }}
                            />
                        }
                        label="I need help with this"
                    />
                </Box>
            );
        }

        if (field.type === 'textarea') {
            return (
                <Box>
                    <FieldRow sx={{ borderBottom: 'none', paddingBottom: '8px' }}>
                        <FieldLabel>
                            {field.label} {field.required && <span style={{ color: '#e31c5f' }}>*</span>}
                        </FieldLabel>
                        {field.aiHelper && (
                            <IconButton
                                size="small"
                                onClick={() => alert(`AI Tip: ${field.aiHelper}`)}
                                sx={{ color: '#717171' }}
                            >
                                <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                        )}
                    </FieldRow>

                    {!isStuck && (
                        <StyledTextField
                            fullWidth
                            multiline
                            rows={4}
                            placeholder={field.placeholder}
                            value={responses[field.id] || ''}
                            onChange={(e) => handleFieldChange(field.id, e.target.value)}
                            disabled={isStuck}
                        />
                    )}

                    <StuckCheckbox
                        control={
                            <Checkbox
                                checked={isStuck}
                                onChange={() => toggleStuck(field.id)}
                                sx={{
                                    color: '#717171',
                                    '&.Mui-checked': { color: '#222222' },
                                }}
                            />
                        }
                        label="I need help with this"
                    />
                </Box>
            );
        }

        if (field.type === 'select' || field.type === 'dropdown') {
            return (
                <Box>
                    <FieldLabel sx={{ marginBottom: '16px' }}>
                        {field.label} {field.required && <span style={{ color: '#e31c5f' }}>*</span>}
                    </FieldLabel>

                    {!isStuck && (
                        <RadioGroup
                            value={responses[field.id] || ''}
                            onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        >
                            {field.options?.map(opt => (
                                <FormControlLabel
                                    key={opt}
                                    value={opt}
                                    control={
                                        <Radio
                                            sx={{
                                                color: '#b0b0b0',
                                                '&.Mui-checked': { color: '#222222' },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ fontSize: '16px', color: '#222222' }}>
                                            {opt}
                                        </Typography>
                                    }
                                    sx={{ marginBottom: '8px' }}
                                />
                            ))}
                        </RadioGroup>
                    )}

                    <StuckCheckbox
                        control={
                            <Checkbox
                                checked={isStuck}
                                onChange={() => toggleStuck(field.id)}
                                sx={{
                                    color: '#717171',
                                    '&.Mui-checked': { color: '#222222' },
                                }}
                            />
                        }
                        label="I need help with this"
                    />
                </Box>
            );
        }

        if (field.type === 'checkbox') {
            return (
                <Box>
                    <FieldLabel sx={{ marginBottom: '16px' }}>
                        {field.label} {field.required && <span style={{ color: '#e31c5f' }}>*</span>}
                    </FieldLabel>

                    {!isStuck && (
                        <RadioGroup
                            value={responses[field.id] === true ? 'yes' : responses[field.id] === false ? 'no' : ''}
                            onChange={(e) => handleFieldChange(field.id, e.target.value === 'yes')}
                        >
                            <FormControlLabel
                                value="yes"
                                control={
                                    <Radio
                                        sx={{
                                            color: '#b0b0b0',
                                            '&.Mui-checked': { color: '#222222' },
                                        }}
                                    />
                                }
                                label={<Typography sx={{ fontSize: '16px', color: '#222222' }}>Yes</Typography>}
                                sx={{ marginBottom: '8px' }}
                            />
                            <FormControlLabel
                                value="no"
                                control={
                                    <Radio
                                        sx={{
                                            color: '#b0b0b0',
                                            '&.Mui-checked': { color: '#222222' },
                                        }}
                                    />
                                }
                                label={<Typography sx={{ fontSize: '16px', color: '#222222' }}>No</Typography>}
                            />
                        </RadioGroup>
                    )}

                    <StuckCheckbox
                        control={
                            <Checkbox
                                checked={isStuck}
                                onChange={() => toggleStuck(field.id)}
                                sx={{
                                    color: '#717171',
                                    '&.Mui-checked': { color: '#222222' },
                                }}
                            />
                        }
                        label="I need help with this"
                    />
                </Box>
            );
        }

        if (field.type === 'display') {
            return (
                <Box
                    sx={{
                        backgroundColor: '#f7f7f7',
                        borderRadius: '12px',
                        padding: '20px',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '14px',
                            color: '#222222',
                            whiteSpace: 'pre-line',
                            lineHeight: 1.6,
                        }}
                    >
                        {field.placeholder || field.label}
                    </Typography>
                </Box>
            );
        }

        // Default fallback
        return (
            <Box>
                <FieldLabel sx={{ marginBottom: '8px' }}>
                    {field.label} {field.required && <span style={{ color: '#e31c5f' }}>*</span>}
                </FieldLabel>
                <StyledTextField
                    fullWidth
                    value={responses[field.id] || ''}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                />
            </Box>
        );
    };

    return (
        <PageContainer>
            {/* Header */}
            <Header>
                <Logo>{questionnaire.title}</Logo>
                <HeaderActions>
                    <HeaderButton
                        variant="outlined"
                        startIcon={<HelpOutlineIcon />}
                        onClick={() => setShowAiWidget(true)}
                    >
                        Questions?
                    </HeaderButton>
                    {user?.userType === 'EO' && <HeaderButton variant="outlined" onClick={async () => {
                        try {
                            await userQuestionnaireService.saveResponses(assignmentId!, responses, false);
                            navigate(`/welcome-questionnaire?assignmentId=${assignmentId}&assignLawyer=1`);
                        } catch { setNotification({ type: 'error', message: 'Could not save your draft. Please try again.' }); }
                    }}>Assign to Lawyer</HeaderButton>}
                    <HeaderButton variant="outlined" onClick={handleSaveAndExit}>
                        Save & exit
                    </HeaderButton>
                </HeaderActions>
            </Header>

            {/* Main Content */}
            <MainContent>
                {notification && (
                    <Alert
                        severity={notification.type}
                        sx={{ width: '100%', mb: 3 }}
                        onClose={() => setNotification(null)}
                    >
                        {notification.message}
                    </Alert>
                )}

                {/* Page Title (Section Name) */}
                <PageTitle>
                    {sections[activeSection] || questionnaire.title}
                </PageTitle>

                {/* Section Title (Step Title) */}
                <Box sx={{ width: '100%' }}>
                    <SectionTitle>
                        {activeStepDef.title}
                    </SectionTitle>

                    {activeStepDef.description && (
                        <Typography
                            sx={{
                                fontSize: '14px',
                                color: '#717171',
                                marginBottom: '24px',
                            }}
                        >
                            {activeStepDef.description}
                        </Typography>
                    )}

                    {/* Review Summary for Review Steps */}
                    {activeStepDef.title.toLowerCase().includes('review') && (
                        <ReviewCard>
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    color: '#717171',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    marginBottom: '16px',
                                }}
                            >
                                Summary of {sections[activeSection]}
                            </Typography>
                            {currentSectionSteps
                                .filter(s => s !== activeStepDef)
                                .map(step => (
                                    <Box key={step.title} sx={{ marginBottom: '16px' }}>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                color: '#222222',
                                                marginBottom: '8px',
                                            }}
                                        >
                                            {step.title}
                                        </Typography>
                                        <Box sx={{ paddingLeft: '16px', borderLeft: '2px solid #ebebeb' }}>
                                            {step.fields.map(f => {
                                                const val = responses[f.id];
                                                if (f.type === 'display') return null;
                                                return (
                                                    <Box key={f.id} sx={{ marginBottom: '8px' }}>
                                                        <Typography sx={{ fontSize: '12px', color: '#717171' }}>
                                                            {f.label}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '14px', color: '#222222' }}>
                                                            {f.type === 'checkbox' ? (val ? 'Yes' : 'No') : (val || '-')}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                ))
                            }
                        </ReviewCard>
                    )}

                    {/* Fields */}
                    <FieldContainer>
                        {activeStepDef.fields.map(field => (
                            <Box key={field.id} sx={{ marginBottom: '24px' }}>
                                {renderField(field)}
                            </Box>
                        ))}
                    </FieldContainer>
                </Box>
            </MainContent>

            {/* Footer */}
            <Footer>
                <ProgressContainer>
                    {steps.map((_, index) => (
                        <ProgressSegment
                            key={index}
                            completed={index < completedSteps}
                            active={index === completedSteps}
                        />
                    ))}
                </ProgressContainer>
                <FooterActions>
                    <BackButton onClick={handleBack} disabled={isFirstStep}>
                        Back
                    </BackButton>
                    <NextButton
                        onClick={handleNext}
                        disabled={saveMutation.isPending}
                    >
                        {saveMutation.isPending
                            ? 'Saving...'
                            : isLastStep
                                ? 'Complete'
                                : 'Next'
                        }
                    </NextButton>
                </FooterActions>
            </Footer>

            {/* AI Helper Widget */}
            <AiHelperWidget
                isOpen={showAiWidget}
                onClose={() => setShowAiWidget(false)}
                hideFab
            />
        </PageContainer>
    );
};

export default QuestionnaireAnswer;
